/**
 * お金まわりの画面の日本語（Functions・X の共有は ja.fnShare.ts に移した・2026-09-28。en.money.ts と同じキーを全部持つ。漏れは typecheck で落ちる）。
 * 表記の決め事は ja.ts の冒頭と同じ。加えて Stripe の名前は出さない（「受け取りの登録」「本人確認」「銀行口座」と言う）。
 */
import type { enMoney } from './en.money';
import type { PluralMsg } from './index';

type MoneyMessages = { [K in keyof typeof enMoney]: (typeof enMoney)[K] extends string ? string : string | PluralMsg };

export const jaMoney: MoneyMessages = {
  // ------------------------------------------------------------ shell
  'money.topbarLink': 'お金',
  // 2026-10-07 追記 7（管理の判断）: 「10% consumption tax」とは書かない（利用者に税を課しているように読める・国も匂う）→ 決まった 1 文を率に依存せずいつも出す。
  //   税は請求書に税が載る事業者の項目だけ（今は Cloudflare）。月末は戻すだけ（追記 2/3）。サイトの料金ページ・CLI・MCP と同じ文（言い回しを足さない）
  'money.pricesFromProviders': '料金は原価どおり（各社が加える税を含む）です。',
  'money.refundOnly': '使った分はその場で残高から引きます。月末に、月の込みの量に入った分が戻ることがあります。後から追加で引くことはありません。',
  'money.navLabel': 'お金',
  'money.account': 'あなたのアカウント',
  'money.accountSub': '売上と利用料',
  'money.accountSubLaunch': '利用料',
  'money.nav.group.earn': '売上',
  'money.nav.group.spend': '利用料',
  'money.nav.group.security': 'セキュリティ',
  'money.nav.earnings': '売上と残高',
  'money.nav.withdraw': '出金',
  'money.nav.getPaid': '受け取りの登録',
  'money.nav.prepaid': '前払い残高',
  'money.nav.usage': '利用料',
  'money.nav.passkeys': 'パスキー',
  'money.mode.legend': 'モード',
  'money.mode.live': '本番',
  'money.mode.test': 'テスト',
  'money.band.testTag': 'テスト',
  'money.band.test': 'テストモード: 開発環境での売上です。実際のお金は動きません。',
  // 2026-09-28: 実際の動き（MASTER-PLAN §2・servingGate.ts）に合わせた（en の注記と同じ）
  'money.owedBanner': '利用料の未払いが{owed}あります。入金するまで、アプリは利用者向けに止まります。入金すると自動で再開します。',
  'money.owedBannerLink': '入金する',

  // ------------------------------------------------------------ earnings
  'earn.title': '売上と残高',
  'earn.lead':
    '売上は返金やチャージバックに備えて30日間保留され、その後に出金できるようになります。{product}の手数料は0%で、引かれるのはカードの決済手数料だけです。',
  'earn.pending': '保留中',
  'earn.reserve': '準備金',
  'earn.pendingHint': '売上ごとに30日後に確定',
  'earn.available': '確定済み',
  'earn.availableHint': '出金できます',
  'earn.availableHintSetup': '受け取りの登録後に出金できます',
  'earn.owedHint': 'マイナス: 出金後の返金分です。次の売上で相殺されます',
  'earn.inTransit': '送金中',
  'earn.inTransitHint': '銀行口座へ送金しています',
  'earn.paid': '出金済み',
  'earn.paidHint': 'これまでの合計',
  'earn.withdraw': '出金する',
  'earn.setUp': '受け取りを登録する',
  'earn.notReady': 'まだ出金できません: {reason}',
  'earn.bySource': '収入源別',
  'earn.byGame': 'プロジェクト別',
  'earn.source.sale': 'アプリ内の購入',
  'earn.source.ad': '広告',
  'earn.source.issuer_reward': '紹介の報酬',
  'earn.source.pass': 'パス',
  'earn.source.self_purchase': '自分の支払い（売上に含めない）',
  'earn.self.tile': '自分の支払い',
  'earn.self.tileHint': '売上に含めない・出金できません',
  'earn.self.title': '自分の支払い（売上に含めない）',
  'earn.self.body':
    'あなたのカード・お名前で支払われた購入は売上に含めず、30 日後に前払い残高へ移します（利用料に使えます・出金はできません）。誤りならサポートへ。',
  'earn.self.byGame': '+ 自分の支払い {amount}（売上に含めない）',
  'earn.self.col.when': '日時',
  'earn.self.col.game': 'プロジェクト',
  'earn.self.col.paid': '支払額',
  'earn.self.col.amount': '取り分',
  'earn.self.col.status': '状態',
  'earn.self.status.excluded': '保留中',
  'earn.self.status.prepaid': '前払いへ移した',
  'earn.self.status.released': '運営が解除',
  'earn.self.none': 'まだありません。',
  'earn.col.source': '収入源',
  'earn.col.count': '件数',
  'earn.col.gross': '総額',
  'earn.col.reversed': '返金',
  'earn.col.net': '純額',
  'earn.col.game': 'プロジェクト',
  'earn.col.sales': '売上',
  'earn.unknownGame': '削除したプロジェクト',
  'earn.emptyTitle': 'まだ売上はありません',
  'earn.emptyBody': 'アプリで購入があったり広告をオンにしたりすると、ここに売上が表示されます。',
  'earn.emptyBodyTest': 'テストモードの売上は、開発環境での購入から集計されます。',
  'earn.payouts': '出金の履歴',
  'earn.noPayouts': 'まだ出金はありません。',
  'earn.col.requested': '依頼日時',
  'earn.col.amount': '金額',
  'earn.col.fee': '手数料',
  'earn.col.status': '状態',
  'earn.col.via': '依頼元',
  'earn.via.dashboard': 'ダッシュボード',
  'earn.via.mcp': 'AIエージェント',
  'earn.instant': '即時',
  'earn.statement': '明細をダウンロード（CSV）',
  'earn.statementHint': '直近12か月の残高の動きすべて',
  'payout.status.draft': '下書き',
  'payout.status.approved': '承認済み',
  'payout.status.transferring': '送金中',
  'payout.status.paid': '着金',
  'payout.status.failed': '失敗',
  'payout.status.reversed': '差し戻し',
  'payout.status.cancelled': '取り消し',

  // ------------------------------------------------------------ payout setup
  'payee.title': '受け取りの登録',
  'payee.lead': '売上の振込先を登録します。本人確認と口座の入力はこのページの中で安全に行われ、決済サービスのアカウントを別に作る必要はありません。',
  'payee.headline.not_started': 'まだ登録していません',
  'payee.headline.needs_info': '追加の情報が必要です',
  'payee.headline.in_review': '確認しています',
  'payee.headline.ready': '受け取りの準備ができています',
  'payee.headline.on_hold': '受け取りを保留しています',
  'payee.headline.closed': '受け取りの登録は閉じられました',
  'payee.body.not_started': '国を選んで始めてください。氏名・生年月日・税務上の番号・銀行口座またはデビットカードを入力します（5分ほど）。',
  'payee.body.needs_info': '本人確認のために、いくつか追加の情報が必要です。続きから入力してください。',
  'payee.body.in_review': '入力は完了です。確認は通常数分、長くても1〜2日で終わります。追加で必要なものがあればメールでお知らせします。',
  'payee.body.ready': '確定済みの売上は、毎日でも・いくらからでも出金できます。',
  'payee.body.on_hold': '運営がこのアカウントの受け取りを一時停止しています。売上は保全されています。メールを確認するか、サポートに連絡してください。',
  'payee.body.closed': 'このアカウントの受け取りの登録は閉じられました。新しく登録するにはサポートに連絡してください。',
  'payee.dueCount': '残り{count}項目',
  'payee.pastDue': '期限を過ぎています。入力を終えるまで出金は止まります。',
  'payee.country': '国',
  'payee.country.US': 'アメリカ合衆国',
  'payee.country.CA': 'カナダ',
  'payee.countryHint': '受け取りは米国とカナダのクリエイターが対象です。',
  'payee.countryLocked': '国: {country}',
  'payee.start': '登録を始める',
  'payee.continue': '続きを入力する',
  'payee.update': '登録内容を確認する',
  'payee.refresh': '状態を確認',
  'payee.checked': '状態を更新しました。',
  'payee.tax': '税務書類',
  'payee.tax.w9': 'W-9',
  'payee.tax.w8ben': 'W-8BEN',
  'payee.taxStatus.missing': '未提出',
  'payee.taxStatus.pending': '確認中',
  'payee.taxStatus.verified': '確認済み',
  'payee.taxStatus.invalid': '要修正',
  'payee.status': '状態',
  'payee.sim.title': '模擬の本人確認',
  'payee.sim.body':
    'このサーバーには決済の鍵が無いため、実際の本人確認は行われず、お金も動きません。本番のサーバーでは、ここに安全な本人確認のフォームが表示されます。',
  'payee.sim.finish': '模擬の登録を完了する',
  'payee.embed.loading': '安全なフォームを読み込んでいます…',
  'payee.embed.close': 'フォームを閉じる',
  'payee.embed.failed': '本人確認のフォームを読み込めませんでした。接続を確認して、もう一度お試しください。',
  'payee.embed.unavailableTitle': 'このサーバーではまだ受け取りを登録できません。',
  'payee.embed.unavailableBody': 'サーバーに{keyVar}の設定が必要です。{product}サーバーの管理者に依頼してください。',
  'payee.testNote': 'テストモード: テスト用の情報を入力してください。実際の口座にはなりません。',

  // ------------------------------------------------------------ withdraw
  'wd.title': '出金',
  'wd.lead': '確定済みの残高から、毎日でも・いくらでも出金できます。出金の手数料は出金額から差し引かれます。',
  'wd.available': '出金できる額',
  'wd.amount': '金額',
  'wd.all': '全額',
  'wd.speed': '速さ',
  'wd.standard': '通常',
  'wd.standardHint': '通常2営業日で着金',
  'wd.instant': '即時',
  'wd.instantHint': '対応するデビットカードへ数分で着金・追加の手数料あり',
  'wd.youReceive': '受け取る額',
  'wd.fee': '出金の手数料',
  'wd.fee.payout': '送金',
  'wd.fee.instant': '即時',
  'wd.fee.crossBorder': '通貨の換算',
  'wd.fee.account': '口座の月額（その月の最初の出金のみ）',
  'wd.feeNote': '手数料は決済ネットワークの実費です。{product}は上乗せしません。',
  'wd.quoting': '計算しています…',
  'wd.continue': '次へ',
  'wd.continueHint': '次の画面でパスキーで確認します。確認するまで送金はされません。',
  'wd.invalid': '25や25.50のように入力してください。',
  'wd.tooMuch': '出金できる額（{amount}）を超えています。',
  'wd.belowFee': 'この金額では出金の手数料をまかなえません。',
  'wd.notReadyTitle': 'まだ出金できません',
  'wd.reason.setup': '受け取りが未登録です',
  'wd.reason.review': '登録内容を確認しています',
  'wd.reason.info': '追加の情報が必要です',
  'wd.reason.tax': '税務情報を確認しています',
  'wd.reason.hold': '受け取りが保留されています',
  'wd.reason.other': '受け取りがまだ有効になっていません',
  'wd.goSetup': '受け取りの登録へ',
  'wd.noPasskeyTitle': '先にパスキーを追加してください',
  'wd.noPasskeyBody': '出金はすべてパスキー（Face ID・Touch ID・Windows Hello・セキュリティキー）で確認します。',
  'wd.addPasskey': 'パスキーを追加',
  'wd.nothingTitle': 'まだ出金できる額はありません',
  'wd.nothingBody': '売上は購入から30日後に確定します。',
  'wd.mcpNote': 'AIエージェント（MCP）に出金を頼むこともできます。その場合も、1件ずつここでパスキーで確認します。',

  // ------------------------------------------------------------ confirm withdrawal
  'cf.eyebrow': '出金',
  'cf.title': '出金の確認',
  'cf.lead': '金額を確かめてから、パスキーで確認してください。確認するまで送金はされません。',
  'cf.fromMcp': 'この出金はAIエージェント（MCP）からの依頼です。自分で頼んだものだけ確認してください。',
  'cf.amount': '残高から引く額',
  'cf.fee': '出金の手数料',
  'cf.net': '受け取る額',
  'cf.speed': '速さ',
  'cf.expires': 'リンクの有効期限',
  'cf.mode': 'モード',
  'cf.confirm': 'パスキーで確認',
  'cf.testConfirm': 'パスキーなしで確認（テスト）',
  'cf.testNote': '本番以外のサーバーのテストモードでは、パスキーなしで確認できます。本番の出金には必ずパスキーが必要です。',
  'cf.cancel': '依頼を取り消す',
  'cf.cancelled': '依頼を取り消しました。お金は動いていません。',
  'cf.sentTitle': '出金しました',
  'cf.sentBody': '{net}を銀行口座へ送金しています。',
  'cf.queuedTitle': '出金を承認しました',
  'cf.queuedBody': '最初の送金がうまくいきませんでした。自動で再試行し、失敗した場合はメールでお知らせします。',
  'cf.failedTitle': '出金に失敗しました',
  'cf.failedBody': '送金できませんでした。金額は出金できる残高に戻っています。',
  'cf.status.executed': 'この出金はすでに確認済みです。',
  'cf.status.confirmed': 'この出金は確認済みで、処理しています。',
  'cf.status.expired': 'このリンクは期限切れです。あらためて出金を依頼してください。',
  'cf.status.cancelled': 'この依頼は取り消されました。',
  'cf.status.rejected': 'この依頼は受け付けられませんでした。',
  'cf.noPasskey': '出金を確認するにはパスキーを追加してください。',
  'cf.notFoundTitle': '出金の依頼が見つかりません',
  'cf.notFoundBody': 'リンクが間違っているか、別の{product}アカウントの依頼です。依頼したアカウントでログインしてください。',
  'cf.toEarnings': '売上と残高へ',
  'cf.newWithdrawal': '新しく出金する',
  'cf.passkeyClosed': 'パスキーの画面が閉じられました。送金はしていません。',

  // ------------------------------------------------------------ passkeys
  'pk.title': 'パスキー',
  'pk.lead':
    'パスキーは出金の確認に使います（このダッシュボードからも、AIエージェント（MCP）からの依頼も）。新しく追加したパスキーで本番の出金を確認できるのは、追加から72時間後です。',
  'pk.add': 'パスキーを追加',
  'pk.addTitle': 'パスキーを追加',
  'pk.label': '名前',
  'pk.labelPlaceholder': '例: MacBookのTouch ID',
  'pk.passwordHint': 'まず本人の確認（パスワード、または Google などのログインのやり直し）をします。ロックしていないPCを他人に使われても、その人のパスキーを追加されないようにするためです。',
  'pk.addConfirm': '次へ',
  'pk.added': 'パスキーを追加しました。本番の出金の確認には{when}から使えます。',
  'pk.unsupported': 'このブラウザはパスキーに対応していません。最新のChrome・Safari・Edge・Firefoxをお使いください。',
  'pk.emptyTitle': 'まだパスキーはありません',
  'pk.emptyBody': '最初の出金の前に追加してください。',
  'pk.col.name': '名前',
  'pk.col.added': '追加日時',
  'pk.col.lastUsed': '最終使用',
  'pk.col.live': '本番の出金',
  'pk.liveReady': '使えます',
  'pk.liveFrom': '{when}から',
  'pk.remove': '削除',
  'pk.removeTitle': '「{label}」を削除しますか？',
  'pk.removeBody': 'このパスキーでは出金を確認できなくなります。',
  'pk.removed': 'パスキーを削除しました。',
  'pk.cancelled': 'パスキーの画面が閉じられました。',

  // ------------------------------------------------------------ prepaid
  'pp.title': '前払い残高',
  // 2026-09-28 founder「抽象的な言い回しはやめていい」: 何が起きるかを書く（en の注記と同じ）
  'pp.lead': 'プロジェクトが使った分の費用を、{product} が各社に払う値段のまま、この残高から差し引きます。最初の 1 回の利用から差し引きます（無料の分はなく、招待クレジットがあれば先にそちらを使います）。入金はいつでもできます。',
  // 2026-10-18 design/rauno-dashboard: 前払い残高の上の 1 行から、下の「料金のしくみ」の段（pp.lead・手数料の説明・例）へのリンクと、その段の見出し
  'pp.howPricing': '料金のしくみ',
  'pp.balance': '残高',
  'pp.balanceHint': '{line}を下回ったらお知らせ',
  'pp.balanceHintOff': 'お知らせはオフ',
  'pp.balanceHintInvite': '招待クレジットを使っている間は、{line} を下回ったらお知らせします（設定: {setting}）。',
  'pp.owed': '未払い',
  'pp.owedHint': '残高を超えて使った分',
  'pp.runway': 'もつ日数の目安',
  'pp.runwayDays': '{count}日',
  'pp.runwayHint': '今月の使い方のペースで',
  'pp.runwayIdle': 'まだ利用なし',
  'pp.runwayIdleHint': '今月の利用はまだありません',
  'pp.runwayZero': '残高なし',
  'pp.expiringSoon': '{amount}が30日以内に期限切れになります',
  'pp.owedTitle': '残高がなくなりました',
  'pp.owedBody': '利用料の未払いが{owed}あります。入金するまで、アプリの利用者向けの処理（公開したアプリ・サーバー・マルチプレイ・AI）が止まり、新しいプロジェクトの作成と公開もできません。入金すると自動で再開します。自動チャージを ON にしておくと止まりません。',
  'pp.lowTitle': '残高が少なくなっています',
  'pp.lowBodyDays': 'いまの使い方ではあと{days}日ほどです。入金するか、自動チャージをオンにしてください。',
  'pp.lowBody': '止まらないように、入金するか自動チャージをオンにしてください。',
  'pp.lowTitleInvite': '招待クレジットの残りがわずかです',
  'pp.lowBodyInvite': '続けて使うには、入金してください。',
  'pp.outTitle': '前払いの残高を使い切りました',
  'pp.outTitleInvite': 'クレジット（招待クレジットを含む）を使い切りました',
  'pp.outBody': '入金するまで、アプリの利用者向けの処理（公開したアプリ・サーバー・マルチプレイ・AI）が止まり、新しいプロジェクトの作成と公開もできません。入金すると自動で再開します。',
  'pp.topUp': '入金',
  'pp.amount': '金額',
  'pp.other': 'その他',
  'pp.otherAmount': 'その他の金額',
  // 2026-10-07 追記 8（founder「B がいいよ」）: 入金の手数料は上乗せ = 選んだ額がきっちり残高に入り、カードにはその額 + 手数料。額はサーバーの見積もり（topup-quote）
  'pp.feeNote': 'カードの決済手数料（約 6%）は上乗せなので、選んだ額がそのまま残高に入ります。実際の手数料が少なければ差は残高に戻り、多くても追加では引きません。',
  'pp.range': '{min}から{max}まで。',
  'pp.feeLine': '残高に {added} を入れる → お支払いは {pay}（カードの手数料 約{fee}）',
  // 2026-10-07 追記 9 A（管理）: 実際の手数料は 2.9% + 30¢ ではない = 見積もりは 6.2%・固定なし（0300）。率は「約 6%」とだけ（国・換算・理由は書かない）。固定の部分が無いので「少額ほど割合が大きい」は言わない
  'pp.feeExamples': '例:',
  'pp.feeExample': '{added} を入れる → お支払い {pay}（手数料 約{fee}）',
  'pp.autoFee': '自動チャージ 1 回あたり: {added} を入れる → お支払いは {pay}（カードの手数料 約{fee}）',
  'pp.saveCard': 'このカードを自動チャージ用に保存する',
  'pp.pay': '{amount}を支払う',
  // 2026-11-05 money/pendingpay: メール未確認の人の入金・自動チャージ ON はサーバーが 403 email_unverified = 押せない形にしてボタンの近くに 1 行（moneyDisplay.ts prepaidFundingLocked）。確かめ方は上の帯（verifyBanner）
  'pp.lockedUnverified': 'メールアドレスを確かめると入金できます。',
  'pp.paying': '支払い画面を開いています…',
  'pp.topUpDone': '前払い残高に{amount}を入金しました。',
  'pp.topUpPending': '支払いを確かめています…',
  'pp.topUpStillChecking': 'カード会社からの支払いの知らせを、まだ待っています。届くと残高に入ります。',
  'pp.topUpCardRefused': 'このカードでは入金できません。別のカードをお試しください。',
  'pp.topUpCanceled': '入金をやめました。請求はされていません。',
  'pp.chargedFee': 'カードへの請求は{charge}（カードの手数料{fee}）でした。',
  'pp.topUpFailed': '入金は完了しませんでした。請求はされていません。',
  'pp.settings': 'お知らせと自動チャージ',
  'pp.lowLine': '残高がこの額を下回ったら知らせる',
  'pp.lowLineHint': 'メールと、つないでいるAIエージェントに知らせます。0にするとお知らせしません。',
  'pp.auto': '自動チャージ',
  'pp.autoWhy': '残高は前払いで、マイナスにはなりません。尽きると、アプリは利用者に対して止まります。止めずに動かし続ける方法が自動チャージです。額は入金と同じ範囲で選べます。',
  'pp.autoOn': 'オン',
  'pp.autoOff': 'オフ',
  'pp.autoBelow': '残高がこの額を下回ったら',
  'pp.autoAmount': '入金する額',
  // 2026-10-07 追記 8: 自動チャージも手数料は上乗せ
  'pp.autoCard': '{card}に、カードの手数料を足して請求します（設定した額がそのまま残高に入ります）。',
  'pp.autoNoCard': '「このカードを保存する」にチェックして一度入金すると、オンにできます。',
  'pp.autoLockedUnverified': 'メールアドレスを確かめるとオンにできます。',
  'pp.autoFailed': '直近の自動チャージは{ago}に失敗しました。次は24時間後にもう一度試します。',
  'pp.autoFund': '売上から先に充てる',
  'pp.autoFundHint': '前払い残高が足りないとき、確定済みの売上から利用料を払います。',
  'pp.save': '保存',
  'pp.saved': '設定を保存しました。',
  'pp.history': '履歴',
  'pp.noHistory': 'まだ動きはありません。',
  'pp.col.when': '日時',
  'pp.col.what': '内容',
  'pp.col.amount': '金額',
  'pp.src.prepaid_topup': '入金',
  'pp.src.usage_charge': '利用料',
  'pp.src.usage_refund': '払い戻し: 月の込みの量に入った分',
  'pp.src.prepaid_fund': '売上から充当',
  'pp.src.prepaid_offset': '返金の補てん',
  'pp.src.prepaid_expire': '期限切れ',
  'pp.src.prepaid_refund': '入金の返金',
  'pp.src.prepaid_dispute': '入金への申し立て',
  'pp.src.prepaid_dispute_won': '申し立ての解決',
  'pp.src.self_purchase_to_prepaid': '自分の支払いから移した',
  'pp.src.invite_credit': '招待クレジット',
  'pp.notice.auto_topup_succeeded': '{ago}に{amount}を自動チャージしました。',
  'pp.notice.auto_topup_failed': '{ago}の自動チャージに失敗しました。',

  // ------------------------------------------------------------ usage
  // 案内の言葉（founder 2026-09-27・MASTER-PLAN §1c）: 「Kumo の利用料（原価どおり）」。原価そのまま・手数料なしを見出しで伝える
  'use.title': '{product} の利用料（原価どおり）',
  'use.lead': 'プロジェクトが使った量と、その額です。ON にしていない機能の費用はかかりません。',
  'use.month': '月',
  'use.charged': '差し引き済み',
  'use.chargedHint': '前払い残高から引いた額',
  'use.metered': '計測した原価',
  'use.meteredHint': '1セント単位に切り上げる前',
  'use.shared': '共有の費用（あなたの分）',
  'use.sharedHint': '月末に、使った量に応じて分けます',
  'use.adjusted': '払い戻し: 月の込みの量に入った分',
  'use.adjustedHint': '残高に戻した額',
  'use.status.open': '集計中',
  'use.status.closed': '確定',
  'use.status.none': '利用なし',
  'use.byPart': '部品別',
  'use.byDay': '日別',
  'use.byDayNote': '日別の額は目安です（その日の使用量×その日の単価）。',
  'use.emptyTitle': '今月の利用はありません',
  'use.emptyBody': 'プロジェクトが使われる（ログイン・セーブ・ページの表示・Functions の呼び出しなど）と、すぐにここに表示されます。',
  'use.refresh': '今すぐ更新',
  'use.updated': '{ago}に更新',
  'use.col.part': '部品',
  'use.col.cost': '原価',
  'use.col.share': '割合',
  'use.chartLabel': '{period}の種類ごと・日ごとの利用料',
  'use.total': '合計',
  'use.other': 'その他',
  // Kumo の定額の機能（2026-09-28 money/slug-redirect・0262）。原価ではなく Kumo の料金（en.money.ts の注記と同じ）
  'use.addons': '{product} の定額の機能',
  'use.addonsNote': '{product} が決めた小さな定額の任意の機能です。上の原価どおりの利用料とは別です。',
  'use.addonsTile': '定額の機能',
  'use.addonsTileHint': '前払い残高から引いた定額の機能の額',
  'use.addon.slug_redirect': '転送（月 $1）',
  'use.addon.slug_redirectDetail': '今月 {count} 日分（古いアドレス 1 つ × 1 日）· 古いアドレス 1 つにつき 1 か月まるごとで $1、日割り',
  'use.addonsRefunded': '訂正で {amount} を戻しました',
  'use.addonsDayNote': '{product} の定額の機能はこのグラフに含みません。',
  'comp.players': 'ログイン・利用者',
  // 2026-09-27 Quality SEC-HIDE-04 / MASTER-PLAN §1c: 最初の公開で隠す機能の名前を文言に残さない（en.money.ts の注記と同じ）。公開したら元の名前に戻す
  'comp.gamedata': 'セーブ・データ',
  'comp.payments': '課金',
  'comp.hosting': 'ホスティング',
  'comp.realtime': 'マルチプレイ',
  'comp.ai': 'その他の機能',
  'comp.ads': '広告',
  'comp.social': 'その他の機能',
  'comp.liveops': 'その他の機能',
  'comp.integrity': '利用者の BAN',
  'comp.compute': 'Functions',
  'comp.share': 'Xの共有',
  'comp.mail_broadcast': '利用者へのメール',
  'comp.platform': '共通',
  'use.meter.functions_log_events': 'ログ',

  // ------------------------------------------------------------ usage view（2026-10-07 console/usage-view・en.money.ts と同じキー）
  'use.freshAgo': '{ago}に更新。',
  'use.freshNever': '今月はまだ更新していません。',
  'use.freshWithin': '新しい利用は約{count}分以内に反映されます。',
  'use.freshHour': '新しい利用は約1時間以内に反映されます。',
  'use.thisMonth': '今月',
  'use.monthTotal': '合計',
  'use.totalHint': '残高から引いた額',
  'use.today': '今日',
  'use.todayHint': '今日のここまで（UTC）',
  'use.returned': '月末に戻った額',
  'use.returnedHint': '残高に戻りました',
  'use.returnedRow': '月末に残高へ戻った額',
  'use.balanceHint': '残高 {amount}',
  'use.runwayHint': '残高 {balance}・今月のペースで',
  'use.runwayEarly': '残高 {balance}。月が始まって{count}日なので、目安は大きく変わることがあります。',
  'use.runwayIdleHint': '残高 {balance}。今月はまだ使っていません。',
  'use.byKind': '種類ごと',
  'use.byKindNote': '使った量と、その額です。',
  'use.byProject': 'プロジェクトごと',
  'use.byProjectNote': '共有プランの費用と定額の機能はアカウント全体の分なので、プロジェクトには分けていません。',
  'use.projectDeleted': '削除したプロジェクト',
  'use.cat.database': 'データベース（セーブ・ログイン・スコア）',
  'use.cat.platform': '{product}の本体（プロジェクトの管理・AIエージェントからの呼び出し）',
  'use.cat.functions': 'サーバーの機能',
  'use.cat.transfer': 'アプリの配信（転送量）',
  'use.cat.storage': 'ファイルの保存',
  'use.cat.multiplayer': '通信対戦',
  'use.cat.domains': '独自ドメイン',
  'use.cat.email': 'メール',
  'use.cat.ai': 'AI',
  'use.cat.x_sign_in': 'X でログイン',
  'use.cat.other': 'その他',
  'use.qty.requests': '{count}回',
  'use.qty.gbTransferred': '{value} GB 送信',
  'use.qty.gbStored': '平均 {value} GB 保存',
  'use.qty.roomHours': 'ルーム {value} 時間',
  'use.qty.playerHours': '利用者の接続 {value} 時間',
  'use.qty.emails': '{count}通',
  'use.qty.domains': '{count}件',
  'use.qty.signIns': '{count}回',
  'topbar.balanceLabel': '残高 {amount}。残高の画面を開く',
  'topbar.balanceLow': '残りわずか',
  'topbar.balanceOut': '残高なし',
  'topbar.balancePending': 'メール確認待ち',
  'topbar.balanceStart': '入金する',
  'topbar.autoTopUp': '自動チャージ ON',
  'overview.usage': '今月の使用額',
  'overview.usageLink': '内訳を見る',

  // ------------------------------------------------------------ project nav extras
  'nav.functions': 'Functions',
  'nav.share': 'Xの共有',
  'pay.payoutsNew': '受け取り',
  'pay.payoutsNewLead':
    'この売上は{product}の残高に入ります。{product}の手数料は0%で、カードの決済手数料だけが引かれます。受け取りの登録と出金は、すべてのプロジェクト共通の「お金」の画面で行います。',
  'pay.openMoney': '「お金」を開く',

  // ------------------------------------------------------------ errors
  'error.passkey_required': 'パスキーで確認してください。まだ無ければ「パスキー」で追加できます。',
  'error.passkey_cooldown': 'このパスキーは追加から72時間たっていないため、まだ本番の出金を確認できません。',
  'error.passkey_invalid': 'パスキーを確認できませんでした。もう一度お試しください。',
  'error.passkey_challenge_expired': 'パスキーの確認の期限が切れました。もう一度お試しください。',
  'error.passkey_unknown': 'このパスキーはこのアカウントに登録されていません。',
  'error.passkey_exists': 'このパスキーはすでに登録されています。',
  'error.too_many_passkeys': 'パスキーの数が上限です。先に1つ削除してください。',
  'error.invalid_password': 'パスワードが正しくありません。',
  'error.payee_not_created': '先に受け取りを登録してください。',
  'error.payee_not_eligible': 'このアカウントの受け取りはまだ有効になっていません。「受け取りの登録」を確認してください。',
  'error.payout_method_cooldown': '振込先を変更してから72時間は出金できません。',
  'error.first_withdrawal_requires_web': '最初の出金はダッシュボードから行ってください。',
  'error.insufficient_available': '出金できる額が足りません。',
  'error.amount_below_fee': 'この金額では出金の手数料をまかなえません。',
  'error.withdrawal_expired': 'この確認のリンクは期限切れです。あらためて出金を依頼してください。',
  'error.withdrawal_not_pending': 'この出金はもう確認待ちではありません。',
  'error.unsupported_country': '受け取りは米国とカナダのみ対応しています。',
  'error.payee_country_locked': '登録を始めた後は国を変更できません。',
  'error.no_saved_payment_method': '「このカードを保存する」にチェックして一度入金してから、自動チャージをオンにしてください。',
  'error.invalid_amount': '金額を確認してください。',
  'error.prepaid_required': '先に前払い残高に入金してください。Functionsの利用料はそこから原価で差し引きます。',
  'error.balance_due': '利用料が未払いです。入金すると再開できます。',
  'error.functions_unavailable': 'この{product}サーバーではFunctionsを動かせません。',
  'error.functions_suspended': '前払い残高がなくなったため、Functionsは停止しています。',
  // ------------------------------------------------------------ dashboard-money2（2026-09-27）
  'money.nav.sales': '売上レポート',
  'money.nav.threeDSecure': 'カードの本人認証',
  'pk.cooldownAll': 'パスキーで本番の出金を確認できるのは {when} からです。テストの出金は今すぐ試せます。',
  'pk.confirmFirst': 'すでにパスキーがあるので、まず今のパスキーで確認し、そのあと新しいパスキーを作ります。パスワードを知っているだけの人が自分のパスキーを足せないようにするためです。',
  'pk.removeVerify': '本人の確認',
  'pk.removeWithPasskey': 'パスキーで確認',
  'pk.removeWithPasskeyHint': '消すパスキー自身を含め、どのパスキーでも確認できます。',
  // 2026-09-28 money/reauth-ui: 削除のもう一方の確認は Creator の再認証の部品（パスワードか Google 等のやり直し）
  'pk.removeWithReauth': 'パスワードか Google などのログインで確認',
  'pk.removeWithReauthHint': '{product}のパスワード、または連携している Google などでのログインのやり直しで確認します。',
  'pk.removeLastPasskeyOnly': '最後のパスキーなので、パスキーでの確認でだけ削除できます（パスワードや Google などのログインだけでは削除できません）。',
  'pk.removeLastUnsupported': '最後のパスキーはパスキーでの確認でだけ削除できますが、このブラウザはパスキーに対応していません。パスキーを使える端末から削除してください。',
  'pk.reauthResumed': '確認済みです。もう一度「次へ」を押してパスキーを作ってください。',
  'cf.cooldownUntil': '追加したばかりのパスキーのため、本番の出金を確認できるのは {when} からです。この依頼はそれより前に期限が切れるので、その時刻を過ぎてから出金をやり直してください。',
  'wd.passkeyCooldown': 'パスキーで本番の出金を確認できるのは {when} からです。今依頼を作ると、それより前に期限が切れます。',
  'bank.adviseShort': '振込先はデビットカードではなく銀行口座にしてください。出金後の返金を差し引けるのは銀行口座だけです。',
  'bank.adviseTitle': '振込先は銀行口座にしてください',
  'bank.adviseBody':
    '出金した後に返金やチャージバックが来た場合、銀行口座なら自動で差し引かれます。デビットカードからは差し引けないため、その額は請求残として残り、次の売上から差し引かれます。デビットカードだけを登録した場合は、振込先の情報を更新して銀行口座を追加してください。',
  'bank.addTitle': '受け取りには銀行口座の登録が必要です',
  'bank.replaceTitle': '銀行口座を追加してください（デビットカードだけでは足りません）',
  'bank.whyBody': '出金後の返金・チャージバックを差し引けるのは銀行口座だけです。振込先の情報を更新して追加してください。',
  'bank.action': '振込先を更新',

  'tds.title': 'カードの本人認証（3Dセキュア）',
  'tds.lead':
    '{product}はすべての支払いでカードの本人認証（3Dセキュア）を求めていますが、実際に行われるかはカード会社次第です。認証された支払いの不正のチャージバックは通常カード会社の責任に移り、それ以外の損失はあなたに残ります。この画面は集計を見るだけで、支払いを止める設定はありません。',
  'tds.group.total': '合計だけ',
  'tds.kind': '支払いの種類',
  'tds.kind.all': 'すべて',
  'tds.kind.purchases': 'アプリ内の購入',
  'tds.kind.topups': '前払いの入金',
  'tds.emptyTitle': 'この期間の支払いはありません',
  'tds.emptyBody': 'アプリでのカードの支払いと、前払い残高への入金がここに出ます。',
  'tds.emptyBodyLaunch': '前払い残高への入金がここに出ます。',
  'tds.payments': '支払い',
  'tds.authShare': '認証あり',
  'tds.authShareHint': 'カード会社が本人を確認した支払いの割合',
  'tds.disputeRate': 'チャージバック率',
  'tds.disputeRateHint': 'チャージバック{count}件（勝訴しても数えます）',
  'tds.unauthDisputeRate': '認証なしのチャージバック率',
  'tds.unauthDisputeRateHint': '損失があなたに残る種類。0.75%を超えたら注意',
  'tds.byResult': '結果ごと',
  'tds.note':
    '「不明」は記録を始める前（2026年9月27日より前）の支払いか、結果を読み取れなかった支払いです。カードブランドはチャージバック率0.75〜1%前後を見ています。支払いが3件未満の率は強調しません。',
  'tds.byGame': 'プロジェクトごと',
  'tds.byMonth': '月ごと',
  'tds.prepaid': '前払いの入金',
  'tds.col.result': '結果',
  'tds.col.payments': '支払い',
  'tds.col.share': '割合',
  'tds.col.authShare': '認証あり',
  'tds.col.unknown': '不明',
  'tds.col.disputes': 'チャージバック',
  // 「購入」→「支払い」（2026-09-30 console/words-pass）: 課金を閉じている間この表は前払いの入金だけ = 購入ではない。英語の Fraud / unrecognized と同じ広さ
  'tds.col.fraud': '不正・覚えのない支払い',
  'tds.col.rate': '率',
  'tds.col.unauthRate': '認証なしの率',
  'tds.row.authenticated': '認証あり',
  'tds.row.notAuthenticated': '認証なし',
  'tds.row.unknown': '不明',
  'tds.result.attempt_acknowledged': '試行のみ（カード会社が未対応）',
  'tds.result.exempted': '免除（低リスク）',
  'tds.result.not_supported': 'カードが非対応',
  'tds.result.failed': '認証に失敗',
  'tds.result.processing_error': '処理エラー',
  'tds.result.data_share_only': 'データ共有のみ',
  'tds.result.none': '試みていない（自動チャージなど）',
  'tds.result.wallet': 'Apple Pay / Google Pay',
  'tds.result.not_card': 'カード以外（Linkなど）',
  'tds.result.other': 'その他',

  'report.title': '売上レポート',
  'report.lead': '期間の売上を、日・月・プロジェクト・収入源ごとに見られます。AI エージェントの売上レポートの道具と同じ数字です。',
  'report.range': '期間',
  'report.lastDays': '直近{count}日',
  'report.groupBy': '切り口',
  'report.group.day': '日',
  'report.group.month': '月',
  'report.group.project': 'プロジェクト',
  'report.group.source': '収入源',
  'report.col.period': '期間',
  'report.col.project': 'プロジェクト',
  'report.col.source': '収入源',
  'report.col.count': '件数',
  'report.col.gross': '総額',
  'report.col.refunds': '返金',
  'report.col.fees': '手数料',
  'report.col.net': '受け取り額',
  'report.total': '合計',
  'report.countHint': '{count}件',
  'report.feesHint': '決済手数料などの実費。{product}の手数料は0%です',
  'report.netHint': '保留中の額を含みます',
  'report.emptyTitle': 'この期間の売上はありません',
  'report.emptyBody': '利用者が購入すると、プロジェクトの売上がここに出ます。',
  'report.note':
    '発生した日で数えます（8月の購入の9月の返金は9月）。ストア課金（App Store・Google Play）はお金が{product}を通らないため含みません。',
  'report.source.purchase': '購入',
  'report.source.network_reward': 'ネットワークの見返り',
  'report.source.ad': '広告',
  'report.source.pass': '{product} Pass',
  'error.passkey_confirmation_required': 'パスキー（削除のときはパスワードや Google などのログインでも可）で確認してから、もう一度お試しください。',
};
